import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import Drawer from '@mui/material/Drawer';
import {useTheme} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {useNavigate} from 'react-router-dom';
import {useAuth} from '../auth/AuthContext';
import {hasAccountingAccess} from '../../subscription';
import {getProfil} from '../../api';
import {PROFILE_UPDATED_EVENT} from '../../events';
import TokenCountdown from '../shared/TokenCountdown';
import NavigationDrawerContent from './appbar/NavigationDrawerContent';
import MobileMenu from './appbar/MobileMenu';
import AppbarActions from './appbar/AppbarActions';
import {NAVIGATION_CONFIG} from './appbar/navigationConfig';
import {buildNavigationSections} from './appbar/navigationUtils';

export const DRAWER_WIDTH = 280;

export default function ButtonAppBar() {
    const {isAuthenticated, user, logout, hasPermission} = useAuth();
    const hasAccounting = hasAccountingAccess(user?.paymentPlan);
    const isAdmin = user?.roles?.includes('ADMIN');
    const canAccessRoleAdmin = isAdmin && (hasPermission ? hasPermission('role:read') : false);
    const canAccessApprovals = hasPermission ? hasPermission('approval:read') : false;
    const navigate = useNavigate();

    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

    const [anchorEl, setAnchorEl] = React.useState(null); // State für das Menü
    const [hasProfile, setHasProfile] = React.useState(false);
    const [expandedSections, setExpandedSections] = React.useState(() => {
        try {
            const raw = localStorage.getItem('navExpandedSections');
            if (raw) {
                return JSON.parse(raw);
            }
        } catch {}
        return {};
    });
    const [profileRefreshIndex, setProfileRefreshIndex] = React.useState(0);

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget); // Ankerpunkt setzen
    };

    const handleMenuClose = () => {
        setAnchorEl(null); // Menü schließen
    };

    const logoutUser = () => {
        logout();
        // Don't send users to /login after logout: in Keycloak-mode that route may redirect to Keycloak.
        navigate("/", { replace: true });
        handleMenuClose(); // Menü schließen nach Logout
    };

    React.useEffect(() => {
        let cancelled = false;

        const fetchProfile = async () => {
            try {
                const res = await getProfil();
                if (cancelled) {
                    return;
                }
                const profile = res?.data;
                setHasProfile(Boolean(profile?.id));
            } catch (error) {
                if (!cancelled) {
                    setHasProfile(false);
                }
            }
        };

        if (isAuthenticated) {
            fetchProfile();
        } else {
            setHasProfile(false);
        }

        return () => {
            cancelled = true;
        };
    }, [isAuthenticated, profileRefreshIndex]);

    React.useEffect(() => {
        const handleProfileUpdated = () => {
            setHasProfile(true);
            setProfileRefreshIndex((prev) => prev + 1);
        };

        window.addEventListener(PROFILE_UPDATED_EVENT, handleProfileUpdated);
        return () => {
            window.removeEventListener(PROFILE_UPDATED_EVENT, handleProfileUpdated);
        };
    }, []);

    const navigationMeta = React.useMemo(() => ({
        hasProfile,
        hasAccounting,
        canAccessRoleAdmin,
        canAccessApprovals,
    }), [hasProfile, hasAccounting, canAccessRoleAdmin, canAccessApprovals]);

    const sections = React.useMemo(() => {
        return buildNavigationSections(NAVIGATION_CONFIG, navigationMeta);
    }, [navigationMeta]);

    React.useEffect(() => {
        setExpandedSections((prev) => {
            const updated = {...prev};
            sections.forEach((section) => {
                if (section.collapsible && typeof updated[section.title] === 'undefined') {
                    updated[section.title] = false;
                }
            });
            return updated;
        });
    }, [sections]);

    const handleMenuItemClick = (path) => {
        navigate(path);
        if (!isDesktop) {
            handleMenuClose();
        }
    };

    const handleToggleSection = (title) => {
        setExpandedSections((prev) => ({
            ...prev,
            [title]: !prev[title],
        }));
    };

    React.useEffect(() => {
        try {
            localStorage.setItem('navExpandedSections', JSON.stringify(expandedSections));
        } catch {}
    }, [expandedSections]);

    return (
        <Box sx={{flexGrow: 1}}>
            {isAuthenticated && isDesktop && (
                <Drawer
                    variant="permanent"
                    anchor="left"
                    open
                    sx={{
                        display: {xs: 'none', md: 'block'},
                        '& .MuiDrawer-paper': {
                            width: DRAWER_WIDTH,
                            boxSizing: 'border-box',
                        },
                    }}
                >
                    <NavigationDrawerContent
                        sections={sections}
                        expandedSections={expandedSections}
                        drawerWidth={DRAWER_WIDTH}
                        onToggleSection={handleToggleSection}
                        onNavigate={handleMenuItemClick}
                    />
                </Drawer>
            )}
            <AppBar
                position="fixed"
                elevation={isAuthenticated ? 4 : 0}
                sx={{
                    backgroundColor: 'orange',
                    color: '#000',
                    ...(isAuthenticated && {
                        width: {md: `calc(100% - ${DRAWER_WIDTH}px)`},
                        ml: {md: `${DRAWER_WIDTH}px`},
                    }),
                    ...(!isAuthenticated && {
                        borderBottom: (t) => `1px solid ${t.palette.divider}`,
                        backdropFilter: "blur(6px)",
                        backgroundColor: 'rgba(255, 165, 0, 0.8)',
                    })
                }}
            >
                <Toolbar sx={{ minHeight: 72 }}>
                    {/* Menü-Icon mit Menü-Funktionalität */}
                    {isAuthenticated && !isDesktop && (
                        <IconButton
                            size="large"
                            edge="start"
                            color="inherit"
                            aria-label="menu"
                            sx={{mr: 2, display: {md: 'none'}}}
                            onClick={handleMenuOpen} // Menü öffnen
                        >
                            <MenuIcon/>
                        </IconButton>
                    )}
                    {isAuthenticated && !isDesktop && (
                        <MobileMenu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            sections={sections}
                            onClose={handleMenuClose}
                            onNavigate={handleMenuItemClick}
                            onLogout={logoutUser}
                        />
                    )}

                    {!isAuthenticated && (
                        <Box
                            sx={{
                                width: 32,
                                height: 32,
                                borderRadius: 2,
                                bgcolor: "orange",
                                border: '2px solid #000',
                                mr: 1.5
                            }}
                        />
                    )}

                    {!isDesktop && isAuthenticated && <TokenCountdown />}

                    {/* Titel der App */}
                    <Typography 
                        variant="h6" 
                        component="div" 
                        sx={{ 
                            flexGrow: 1, 
                            fontWeight: 800,
                            cursor: 'pointer'
                        }}
                        onClick={() => navigate("/")}
                    >
                        Schreiberlein
                    </Typography>

                    <AppbarActions
                        isAuthenticated={isAuthenticated}
                        isDesktop={isDesktop}
                        user={user}
                        onLogout={logoutUser}
                        onNavigate={navigate}
                    />
                </Toolbar>
            </AppBar>
        </Box>
    );
}
